"use client";

import { Bell, Inbox, Shield } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTransition } from "react";
import {
	type EditorialNavLink,
	isNavLinkActive,
} from "@/components/editorial-system/editorial-nav-shared";
import { Avatar } from "@/components/editorial-system/editorial-primitives";
import { EditorialThemeToggle } from "@/components/editorial-system/editorial-theme-toggle";
import { signOutAction } from "@/lib/actions/auth";
import { cn } from "@/lib/utils";

const ICONS = { messages: Inbox, admin: Shield, notifications: Bell } as const;
/** Stacked top to bottom in this order. */
const ORDER = ["messages", "notifications", "admin"] as const;

/**
 * Bottom-left account links: Messages, Notifications and Admin as icons
 * stacked in a column, then the member's avatar and name, and Log out.
 */
export function EditorialAccountNav({
	links,
	className,
}: {
	links: EditorialNavLink[];
	className?: string;
}) {
	const pathname = usePathname() ?? "/";
	const [signingOut, startSignOut] = useTransition();
	const iconLinks = links
		.filter((link) => link.icon)
		.sort(
			(a, b) =>
				ORDER.indexOf(a.icon as (typeof ORDER)[number]) -
				ORDER.indexOf(b.icon as (typeof ORDER)[number]),
		);
	const textLinks = links.filter((link) => !link.icon);

	return (
		<nav aria-label="Your account" className={cn("ed-account-nav", className)}>
			<ul className="ed-account-nav__icons">
				{iconLinks.map((link) => {
					const Icon = ICONS[link.icon as keyof typeof ICONS];
					const active = isNavLinkActive(link, pathname);
					return (
						<li key={link.href}>
							<Link
								href={link.href}
								aria-label={
									link.badge
										? `${link.label}, ${link.badge > 99 ? "99+" : link.badge} unread`
										: link.label
								}
								title={link.label}
								aria-current={active ? "page" : undefined}
								className={cn("ed-account-nav__icon", active && "ed-active")}
							>
								<Icon aria-hidden="true" strokeWidth={1.6} />
								{link.badge ? (
									<span className="ed-account-nav__badge" aria-hidden>
										{link.badge > 99 ? "99+" : link.badge}
									</span>
								) : null}
							</Link>
						</li>
					);
				})}
				<li>
					<EditorialThemeToggle />
				</li>
			</ul>
			{textLinks.map((link) => {
				const active = isNavLinkActive(link, pathname);
				return (
					<Link
						key={link.href}
						href={link.href}
						aria-current={active ? "page" : undefined}
						className={cn("ed-nav ed-row", active && "ed-active")}
						style={{ gap: 8 }}
					>
						{link.avatarUrl !== undefined ? (
							<Avatar
								person={{ name: link.label, avatarUrl: link.avatarUrl }}
								size={20}
							/>
						) : null}
						{link.label}
					</Link>
				);
			})}
			{textLinks.length ? (
				<button
					type="button"
					className="ed-button-text ed-nav ed-secondary"
					onClick={() =>
						startSignOut(async () => {
							await signOutAction();
						})
					}
					disabled={signingOut}
				>
					{signingOut ? "Logging out…" : "Log out"}
				</button>
			) : null}
		</nav>
	);
}
